/* @flow */
import idx from 'idx';
import invariant from 'invariant';
import * as React from 'react';
import { graphql, compose } from 'react-apollo';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import AdminNewCardModal from 'admin/components/AdminNewCardModal';
import adminBillingOverviewQuery from 'admin/queries/adminBillingOverviewQuery.gql';
import AdminBillingContactForm from 'admin/components/AdminBillingContactForm';
import BankCard from 'components/BankCard';
import Button from 'components/ButtonDeprecated';
import Container from 'components/Container';
import Copy from 'components/Copy';
import Divider from 'components/Divider';
import EventTracking from 'lib/EventTracking';
import Field from 'components/Field';
import Link from 'components/Link';
import LoadingIndicator from 'components/LoadingIndicator';
import MailTo from 'components/MailTo';
import Text from 'components/Text';
import TitleHeader from 'components/TitleHeader';
import LinkConstants from 'constants/LinkConstants';
import RemindAPI from 'lib/RemindAPI';
import awaitData from 'lib/awaitData';
import { showAPIErrorToast, showToast } from 'reducers/toasts';

type Card = {
  lastFour: string,
  expiryMonth: number,
  expiryYear: number,
  zip: string,
  brand: any,
  declined: boolean,
};

export type BillingContact = {
  firstName: string,
  lastName: string,
  phoneNumber: string,
  email: string,
};

type Props = {
  showAPIErrorToast: (error: any) => any,
  showToast: (text: string, kind: 'success' | 'error') => any,
  organization: {
    id: Backbone$Id,
    uuid: string,
    name: string,
    paid: boolean,
    type: string,
    permissions: ?Object,
    billingCreditCard: ?Card,
    billingContact: ?BillingContact,
  },
  orgId: number,
};

class AdminBillingOverview extends React.Component<Props> {
  requestInformation = () => {
    const { organization } = this.props;
    invariant(organization, 'organization cannot be null in click handler');

    return RemindAPI.request(
      `/organizations/${organization.id}/get_admin_console_promo`,
      'post'
    ).then(
      () => this.props.showToast('Your request has been sent! We’ll be in touch', 'success'),
      error =>
        error && error.code === 'throttling_error'
          ? this.props.showToast('Your request has been sent! We’ll be in touch', 'success')
          : this.props.showAPIErrorToast(error)
    );
  };

  renderOldBody() {
    const { organization } = this.props;

    if (!organization.paid) {
      console.error('admin billing page - paid only account should to here');
      return <span>Uh, oh. You should not be here, something weird happened.</span>;
    }

    const orgType = organization.type.toLowerCase();
    const emailSubject = `${organization.name} - Billing assistance`;
    const contactUsLine = `Your plan includes access to the Admin Console and other features for your ${orgType}. To discuss details about your plan, please contact our team at ${LinkConstants.DISTRICTS_EMAIL} with subject "${emailSubject}".`;

    return (
      <div>
        <Text element="p" size="medium">
          You currently have a <Text weight="semibold">Remind {orgType} plan</Text> which includes
          access to the Admin Console.
        </Text>
        <Text element="p" size="medium">
          {contactUsLine}
        </Text>
        <MailTo subject={emailSubject} to={LinkConstants.BILLING_EMAIL}>
          <Button>Contact us</Button>
        </MailTo>
      </div>
    );
  }

  renderBody() {
    const { organization } = this.props;
    const canSetCC = idx(organization, _ => _.permissions.adminConsole.setBillingCreditCard);

    return (
      <Container align="left" size="large">
        {this.renderOverview()}
        {canSetCC ? (
          <React.Fragment>
            <Divider />
            {this.renderPayment()}
            <Divider />
            <AdminBillingContactForm
              billingContact={organization.billingContact}
              orgId={organization.id}
            />
          </React.Fragment>
        ) : null}
      </Container>
    );
  }

  handleSupportLinkClicked = () => {
    EventTracking.track('billing.contact_form.clicked', {
      organization_uuid: this.props.organization.uuid,
    });
  };

  renderOverview() {
    const { organization } = this.props;
    // $FlowIssue
    const { type: orgType } = organization;

    return (
      <section>
        <TitleHeader textAlign="left" title="Overview" />
        <Copy>
          <Text size="medium">
            <p>
              Your {orgType.toLowerCase()} is on the{' '}
              <Text weight="semibold">Remind {orgType} Plan</Text> billed annually. To update your
              plan, please fill out this{' '}
              <Link
                href={LinkConstants.PLANS_OPTIONS}
                weight="semibold"
                target="_blank"
                onClick={this.handleSupportLinkClicked}>
                form
              </Link>
              .
            </p>
            <Text color="graphite" element="div">
              <ul>
                {orgType.toLowerCase() === 'district' && (
                  <li>
                    <p>
                      Schools on Remind School Plans (
                      <Link to={`/organizations/${organization.id}/schools`} weight="semibold">
                        view all
                      </Link>
                      )
                    </p>
                  </li>
                )}
                <li>
                  <p>Schoolwide rostering</p>
                </li>
                <li>
                  <p>Long messages</p>
                </li>
                <li>
                  <p>Admin Dashboard access</p>
                </li>
                <li>
                  <p>Communication logs</p>
                </li>
              </ul>
            </Text>
          </Text>
        </Copy>
      </section>
    );
  }

  renderPayment() {
    const { billingCreditCard: card } = this.props.organization;
    const addSubtext = 'Add a card to your account to begin automatic renewals.';
    const updateSubtext = 'Enter your card information to update the current one.';

    let cardValidation;

    if (card && card.declined) {
      cardValidation =
        'There was a problem charging your card. Please provide a valid card to apply to your account.';
    }

    const updateModal = (
      <AdminNewCardModal
        title="Update your card"
        subtext={updateSubtext}
        orgUuid={this.props.organization.uuid}
        orgId={this.props.organization.id}
        actionText="Replace card"
      />
    );

    const addModal = (
      <AdminNewCardModal
        title="Add a card"
        subtext={addSubtext}
        orgUuid={this.props.organization.uuid}
        orgId={this.props.organization.id}
        actionText="Add card"
      />
    );

    const body = card ? (
      <React.Fragment>
        <Field error={cardValidation}>
          <BankCard updateModal={updateModal} {...card} />
        </Field>
      </React.Fragment>
    ) : (
      <React.Fragment>
        <Text element="p" size="medium">
          You will receive an invoice for your renewal. To change the payment method, add a credit
          card to your account.
        </Text>
        <BankCard addModal={addModal} />
      </React.Fragment>
    );

    return (
      <section>
        <TitleHeader textAlign="left" title="Payment details" />
        {body}
      </section>
    );
  }

  render() {
    const seeV2 = idx(
      this.props.organization,
      _ => _.permissions.adminConsole.seeSettingsTabBillingV2
    );
    const body = seeV2 ? this.renderBody() : this.renderOldBody();

    return <div className="AdminBillingOverview">{body}</div>;
  }
}

const withOrganization = graphql(adminBillingOverviewQuery, {
  options: ({ orgId }) => ({
    variables: {
      organizationId: [orgId],
    },
  }),
  props: ({ data }) => ({
    organization: idx(data, _ => _.organizations[0]),
    ...data,
  }),
});

function mapDispatchToProps(dispatch: Function) {
  return bindActionCreators({ showToast, showAPIErrorToast }, dispatch);
}

export { AdminBillingOverview };
export default compose(
  withOrganization,
  awaitData(['organization'], <LoadingIndicator height={400} size="small" />),
  connect(undefined, mapDispatchToProps)
)(AdminBillingOverview);
